import { useEffect, useState, type RefObject } from 'react';

/**
 * The contents rail shared by the two long-form reading screens: a level's
 * debrief (components/debrief) and a chapter's opener (components/opener).
 *
 * Both render author-written markdown into a scrolling column and both want
 * the same two things from it — a list of its `##` headings, and which one the
 * reader is currently under. The logic was written twice, once in each screen,
 * and it is here now because two callers is enough to know which parts of it
 * are the shape and which were that screen's own.
 *
 * What is deliberately *not* here is the markup. The two rails carry different
 * class names against different stylesheets (`.debrief-toc`, `.opener-toc`),
 * and a shared component taking a class name as a prop would be a worse trade
 * than fifteen duplicated lines of `<nav><ol><li>`: it would put a decision
 * about what a rail looks like one indirection away from the stylesheet that
 * makes it look like that.
 */

export interface Heading {
  id: string;
  text: string;
}

/** How far down the viewport a heading has to pass before it counts as current. */
const MARK = 0.25;

/**
 * A heading's id, derived from its text. Collisions are resolved with a
 * counter by the caller rather than tolerated: two sections with the same name
 * would otherwise give the contents two entries that jump to the same place.
 *
 * ## Why this is not `[^a-z0-9]`
 *
 * It was, and in Arabic it produced `section`, `section-2`, `section-3` — one
 * anchor per heading, none of them naming anything. The rail still worked,
 * because the id it links to is the id it wrote; what was lost was the URL. A
 * fragment is the part of a link a person reads before they click it and the
 * part they paste into a message to say *this bit*, and `#section-4` says
 * nothing in any language.
 *
 * So: letters and digits, in every script, and a separator for everything
 * else. The two steps before that are what keep the result stable —
 *
 * `normalize('NFKD')` splits a composed character into its base and its
 * marks, and folds the compatibility forms (an Arabic presentation-form
 * letter, a ligature) onto the ordinary letters they are typed as. Stripping
 * `\p{M}` then drops the marks themselves: an accent in Latin, and the
 * harakat in Arabic, which are optional in writing and so cannot be allowed
 * to change a heading's id — `مُستوى` and `مستوى` are the same word and must
 * be the same anchor. It also folds أ إ آ onto ا, which is the same argument.
 *
 * ASCII is untouched by all of it: NFKD leaves ASCII alone, ASCII has no
 * marks, and `\p{L}\p{Nd}` matches `a-z0-9` there exactly. Every English
 * anchor this ever produced, it still produces.
 */
export function headingSlug(text: string): string {
  return (
    text
      .normalize('NFKD')
      .replace(/\p{M}+/gu, '')
      .toLowerCase()
      .replace(/[^\p{L}\p{Nd}]+/gu, '-')
      .replace(/^-+|-+$/g, '') || 'section'
  );
}

/**
 * The headings of the rendered article in `body`, and which one the reader is
 * under.
 *
 * **Read back off the DOM, never parsed out of the markdown beside it.** Two
 * passes over one text can disagree — about a `##` inside a fenced block, most
 * obviously — and the one that would be wrong is the one the reader clicks.
 * Reading the DOM cannot disagree with the DOM. It also means this assigns the
 * ids: the `id` on each `<h2>` is written here, so the anchor in the rail and
 * the element it lands on are the same string by construction.
 *
 * `when` is the caller's own signal that the rendered content has changed, and
 * it exists because the two screens hold their article differently: the
 * debrief has a bare string, the opener has it inside a fetched object. Either
 * can be passed as-is. It is compared by identity, so a caller that rebuilds an
 * equal object every render will re-scan every render — pass the thing that
 * actually changes, not a fresh wrapper around it.
 *
 * Returns an empty list until there is something rendered to read, which is the
 * right answer for "still loading" and for "this screen is showing a gate
 * instead" alike.
 */
export function useContents(body: RefObject<HTMLElement | null>, when: unknown): { headings: Heading[]; active: string | null } {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const el = body.current;
    if (!el || !when) {
      setHeadings([]);
      setActive(null);
      return;
    }
    const seen = new Map<string, number>();
    const found: Heading[] = [];
    for (const h of Array.from(el.querySelectorAll('h2'))) {
      const label = h.textContent?.trim() ?? '';
      if (!label) continue;
      const base = headingSlug(label);
      const n = seen.get(base) ?? 0;
      seen.set(base, n + 1);
      const id = n === 0 ? base : `${base}-${n + 1}`;
      h.id = id;
      found.push({ id, text: label });
    }
    setHeadings(found);
    setActive(found[0]?.id ?? null);
    // `body` is a ref object and stable for the life of the component; `when`
    // is what actually says the content changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [when]);

  /**
   * Which section the reader is in. The top quarter of the viewport is the
   * mark: a heading counts as current once it has passed it, which is how a
   * reader perceives "where I am" — not when the heading is centred, and not
   * when it leaves the screen entirely.
   */
  useEffect(() => {
    if (headings.length === 0) return;
    const onScroll = () => {
      const mark = window.innerHeight * MARK;
      let current = headings[0]!.id;
      for (const h of headings) {
        const el = document.getElementById(h.id);
        if (el && el.getBoundingClientRect().top <= mark) current = h.id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [headings]);

  return { headings, active };
}
